/**
 * Sub-tabs — one pane visible at a time, scrolling inside the viewport.
 *
 * Extracted from character.js, which had the only working copy of this. The
 * Command and Syndicate pages needed the same behaviour and gear.js already had
 * a second variant, so a third and fourth would have been four implementations
 * of one idea — and the subtle part (the sizing retry below) is exactly what
 * gets dropped when a pattern is re-typed rather than reused.
 *
 * Two jobs, and the second is the one that matters for tablets:
 *
 *   1. Show one pane, hide the rest, mark the active button.
 *   2. Cap the active pane's scroller at the viewport bottom, so a long list
 *      scrolls INSIDE the pane instead of growing the page. That is what keeps
 *      a page fitting a 768px-tall viewport regardless of how much content it
 *      grows later.
 */

import { debounce } from './util.js';

/** groupId -> { panes, scrollers, active, onShow } */
const groups = new Map();

/**
 * @param {string} groupId
 * @param {object} config
 * @param {Object<string,string>} config.panes     - tab name -> pane element id
 * @param {Object<string,string>} [config.scrollers] - tab name -> the element that
 *   should scroll internally. Defaults to the pane itself.
 * @param {string} [config.defaultTab]             - defaults to the first pane
 * @param {(name:string)=>void} [config.onShow]    - re-render hook for the pane
 *   being revealed. Panes are hidden, not unmounted, so anything time-sensitive
 *   has to be refreshed here rather than trusted to be current.
 */
export function registerSubTabs(groupId, { panes, scrollers = {}, defaultTab = null, onShow = null }) {
    groups.set(groupId, {
        panes, scrollers, onShow,
        active: defaultTab || Object.keys(panes)[0],
    });
}

export function activeSubTab(groupId) {
    return groups.get(groupId)?.active || null;
}

export function applySubTabs(groupId) {
    const group = groups.get(groupId);
    if (!group) return;

    for (const [name, elementId] of Object.entries(group.panes)) {
        document.getElementById(elementId)?.classList.toggle('hidden', name !== group.active);
    }
    document.querySelectorAll(`[data-subtab-group="${groupId}"]`).forEach(btn => {
        btn.classList.toggle('active', btn.dataset.subtab === group.active);
    });

    group.onShow?.(group.active);
    sizeActiveScroller(groupId);
}

export function setSubTab(groupId, name) {
    const group = groups.get(groupId);
    if (!group || !(name in group.panes)) return;
    group.active = name;
    applySubTabs(groupId);
}

/**
 * Cap the active scroller at the viewport bottom.
 *
 * Measured from the element's own document offset rather than a fixed header
 * height, so it is immune to whatever sits above it (a stats strip, a founding
 * banner) changing height.
 *
 * The retry is load-bearing: during a boot reveal or a tab fade-in the element
 * is not laid out yet and measures 0, and writing that would clamp the pane to
 * the 200px floor permanently. Bounded so a genuinely hidden pane cannot spin.
 */
const sizeRaf = new Map();

function sizeActiveScroller(groupId, attempt = 0) {
    const group = groups.get(groupId);
    if (!group) return;

    const pending = sizeRaf.get(groupId);
    if (pending) { cancelAnimationFrame(pending); sizeRaf.delete(groupId); }

    const elementId = group.scrollers[group.active] || group.panes[group.active];
    const el = document.getElementById(elementId);
    if (!el) return;

    const absTop = el.offsetParent === null ? 0 : el.getBoundingClientRect().top + window.scrollY;
    if (absTop <= 0) {
        if (attempt < 30) sizeRaf.set(groupId, requestAnimationFrame(() => sizeActiveScroller(groupId, attempt + 1)));
        return;
    }
    el.style.maxHeight = `${Math.max(200, window.innerHeight - absTop - BOTTOM_GAP - chromeBelow(el))}px`;
    el.style.overflowY = 'auto';
}

/** Breathing room under the scroller, so it never sits flush to the fold. */
const BOTTOM_GAP = 12;

/**
 * Padding, border and margin BELOW the scroller, all the way out to <body>.
 *
 * Clamping the scroller to the viewport is not enough on its own: the panes wrap
 * it in padding and a 4px border, and the page container adds its own bottom
 * padding — the page still overflowed by exactly that chrome. Walking to body
 * rather than stopping at `.tab-content` is what closes the last few pixels.
 *
 * Read from computed STYLE, not measured geometry: this runs while the scroller
 * is mid-clamp, and a geometric read would feed its own result back in.
 */
function chromeBelow(el) {
    let total = 0;
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
        const cs = getComputedStyle(node);
        total += parseFloat(cs.paddingBottom) || 0;
        total += parseFloat(cs.borderBottomWidth) || 0;
        total += parseFloat(cs.marginBottom) || 0;
    }
    return total;
}

/** Re-measure every registered group. Cheap: only the active pane measures. */
export function resizeSubTabs() {
    for (const groupId of groups.keys()) sizeActiveScroller(groupId);
}

if (typeof window !== 'undefined') {
    window.setSubTab = setSubTab;
    window.addEventListener('resize', debounce(resizeSubTabs, 100));
}
